import { useRef, useState, useMemo } from 'react';
import convert from 'heic-convert/browser';
import EmojiPicker from 'emoji-picker-react';

const MIME_BY_EXT = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
  gif: 'image/gif',
  avif: 'image/avif',
  pdf: 'application/pdf',
  txt: 'text/plain',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  webm: 'video/webm',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xls: 'application/vnd.ms-excel',
  csv: 'text/csv',
};

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
}

function arrayBufferToBase64(buffer, mimeType = 'image/jpeg') {
  return new Promise((resolve) => {
    const blob = new Blob([buffer], { type: mimeType });
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}

export const MiniUploaderButton = ({
  iconClassName = '',
  isImageUpload = false,
  multiple = false,
  maxFileSizeMB = 10,
  maxImageSizeMB = 2,
  allFormatsAllowed = false,
  allowedFormats = ['jpg', 'jpeg', 'png', 'webm', 'heic', 'pdf', 'mp4', 'webm', 'mov'],
  onFilesAdd,
}) => {
  const fileInputRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const accept = useMemo(() => {
    if (allFormatsAllowed || !Array.isArray(allowedFormats) || allowedFormats.length === 0) {
      return undefined;
    }
    const tokens = new Set();
    for (const extRaw of allowedFormats) {
      if (!extRaw) continue;
      const ext = String(extRaw).toLowerCase().replace(/^\./, '');
      const mime = MIME_BY_EXT[ext];
      if (mime) tokens.add(mime);
      tokens.add(`.${ext}`);
      if (ext === 'heic') tokens.add('image/heif');
    }
    return Array.from(tokens).join(',');
  }, [allowedFormats, allFormatsAllowed]);

  const handleClick = () => {
    if (isProcessing) return;
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const chosenFiles = Array.from(e.target.files || []);
    if (!chosenFiles.length) return;
    setIsProcessing(true);

    const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'gif', 'avif']);
    const finalFiles = [];

    for (const rawFile of chosenFiles) {
      const ext = rawFile.name.split('.').pop().toLowerCase();
      const sizeMB = rawFile.size / (1024 * 1024);

      if (!allFormatsAllowed && !allowedFormats.includes(ext)) {
        continue;
      }

      const isLikelyImage = IMAGE_EXTS.has(ext);
      if (isLikelyImage) {
        if (sizeMB > maxImageSizeMB) {
          continue;
        }
      } else {
        if (sizeMB > maxFileSizeMB) {
          continue;
        }
      }

      let finalFile = rawFile;
      let previewExtension = ext;
      let base64Preview = null;
      let base64File = null;

      try {
        if (ext === 'heic') {
          const inputBuffer = new Uint8Array(await rawFile.arrayBuffer());
          const outputBuffer = await convert({
            buffer: inputBuffer,
            format: 'JPEG',
            quality: 1,
          });
          const newBlob = new Blob([outputBuffer], { type: 'image/jpeg' });
          finalFile = new File([newBlob], rawFile.name.replace(/\.heic$/, '.jpg'), {
            type: 'image/jpeg',
          });
          previewExtension = 'jpg';
          base64Preview = await arrayBufferToBase64(outputBuffer, 'image/jpeg');
        } else if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'avif'].includes(ext)) {
          base64Preview = await fileToBase64(finalFile);
        } else if (['pdf', 'mp4', 'webm', 'mov'].includes(ext)) {
          base64File = await fileToBase64(finalFile);
        }

        finalFiles.push({
          file: finalFile,
          name: finalFile.name,
          sizeMB: sizeMB.toFixed(2),
          type: finalFile.type || '',
          extension: previewExtension,
          base64Preview: base64Preview,
          base64File: base64File,
          loading: false,
        });
      } catch (err) {
        console.error('Failed to process file:', err);
      }
    }

    setIsProcessing(false);

    if (finalFiles.length > 0 && typeof onFilesAdd === 'function') {
      onFilesAdd(finalFiles);
    }
  };

  return (
    <>
      <button
        className={iconClassName}
        onClick={handleClick}
        disabled={isProcessing}
        style={{
          backgroundColor: 'transparent',
          border: 'none',
          padding: 0,
          margin: 0,
          lineHeight: 0,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      />
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        multiple={multiple}
        accept={accept}
        onChange={handleFileChange}
      />
    </>
  );
};

const DraftFilesPreview = ({ files, onRemoveOne, onRemoveAll }) => {
  if (!files || files.length === 0) return null;

  return (
    <div
      className="draft-new-uploads-wrapper"
      style={{
        position: 'relative',
        display: 'inline-block',
        marginTop: '8px',
        marginBottom: '8px',
        backgroundColor: 'transparent',
      }}
    >
      <div
        className="draft-new-uploads-preview"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          rowGap: '8px',
          columnGap: '8px',
          maxWidth: '100%',
        }}
      >
        {files.map((item, idx) => {
          const isImage = !!item.base64Preview;

          if (isImage) {
            return (
              <div
                className="file-preview-item draft"
                key={idx}
                style={{
                  position: 'relative',
                  display: 'inline-flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  maxWidth: '120px',
                }}
              >
                <div
                  className="image-container"
                  style={{
                    borderRadius: '8px',
                    overflow: 'hidden',
                    width: '80px',
                    height: '80px',
                    backgroundColor: '#1f1f1f',
                    border: '1px solid rgba(255,255,255,0.3)',
                  }}
                >
                  <img
                    src={item.base64Preview}
                    alt={item.name}
                    className="image-preview"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </div>

                <button
                  className="remove-file-button"
                  onClick={() => onRemoveOne(idx)}
                  style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    width: '24px',
                    height: '24px',
                    borderRadius: '9999px',
                    border: '1px solid rgba(255,255,255,0.6)',
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    color: '#fff',
                    fontSize: '12px',
                    lineHeight: '22px',
                    textAlign: 'center',
                    cursor: 'pointer',
                  }}
                >
                  ✕
                </button>
              </div>
            );
          }

          return (
            <div
              className="file-preview-item draft file"
              key={idx}
              style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                backgroundColor: 'rgba(255,255,255,0.05)',
                borderRadius: '8px',
                padding: '8px 10px',
                maxWidth: '220px',
                border: '1px solid rgba(255,255,255,0.15)',
              }}
            >
              <button
                className="remove-file-button"
                onClick={() => onRemoveOne(idx)}
                style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  width: '24px',
                  height: '24px',
                  borderRadius: '9999px',
                  border: '1px solid rgba(255,255,255,0.6)',
                  backgroundColor: 'rgba(0,0,0,0.8)',
                  color: '#fff',
                  fontSize: '12px',
                  lineHeight: '22px',
                  textAlign: 'center',
                  cursor: 'pointer',
                }}
              >
                ✕
              </button>

              <div
                className="file-icon"
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '6px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  color: 'white',
                  marginRight: '8px',
                  textTransform: 'uppercase',
                  border: '1px solid rgba(255,255,255,0.2)',
                }}
              >
                {item.extension || 'file'}
              </div>

              <div className="file-info" style={{ minWidth: 0 }}>
                <div
                  className="file-name"
                  style={{
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.9)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '140px',
                  }}
                >
                  {item.name}
                </div>
                <div
                  className="file-creds"
                  style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}
                >
                  <span className="file-ext">{item.extension}</span>
                  <span style={{ margin: '0 4px' }}>•</span>
                  <span className="file-size">{item.sizeMB}MB</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const MessageComposeToolbar = ({
  onEmoji,
  onAddFiles,
  wrapperClassName = 'chat-options',
  emojiButtonClassName = 'emozy-button',
  imageButtonClassName = 'attach-file-button image-uploader',
  fileButtonClassName = 'attach-file-button',
}) => {
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [localDraftFiles, setLocalDraftFiles] = useState([]);

  const toggleEmojiPicker = () => {
    setIsEmojiPickerOpen((prev) => !prev);
  };

  const handleEmojiClick = (emojiData) => {
    if (onEmoji) {
      onEmoji(emojiData.emoji);
    }
    setIsEmojiPickerOpen(false);
  };

  const handleAddImages = (filesArr) => {
    setLocalDraftFiles((prev) => [...prev, ...filesArr]);
    if (onAddFiles) {
      onAddFiles(filesArr);
    }
  };

  const handleAddOtherFiles = (filesArr) => {
    setLocalDraftFiles((prev) => [...prev, ...filesArr]);
    if (onAddFiles) {
      onAddFiles(filesArr);
    }
  };

  const handleRemoveOneLocal = (indexToRemove) => {
    setLocalDraftFiles((prev) => {
      const copy = [...prev];
      copy.splice(indexToRemove, 1);
      return copy;
    });
  };

  const handleRemoveAllLocal = () => {
    setLocalDraftFiles([]);
  };

  const cleanButtonStyle = {
    backgroundColor: 'transparent',
    border: 'none',
    padding: 0,
    margin: 0,
    lineHeight: 0,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  return (
    <div style={{ width: '100%' }}>
      <DraftFilesPreview
        files={localDraftFiles}
        onRemoveOne={handleRemoveOneLocal}
        onRemoveAll={handleRemoveAllLocal}
      />
      <div className={wrapperClassName} style={{ position: 'relative' }}>
        <button
          className={emojiButtonClassName}
          onClick={toggleEmojiPicker}
          style={cleanButtonStyle}
        />

        <MiniUploaderButton
          iconClassName={imageButtonClassName}
          isImageUpload={true}
          multiple={true}
          maxFileSizeMB={10}
          maxImageSizeMB={2}
          allFormatsAllowed={false}
          allowedFormats={['jpg', 'jpeg', 'png', 'webp', 'heic', 'webm']}
          onFilesAdd={handleAddImages}
        />

        <MiniUploaderButton
          iconClassName={fileButtonClassName}
          isImageUpload={false}
          multiple={false}
          maxFileSizeMB={10}
          maxImageSizeMB={2}
          allFormatsAllowed={false}
          allowedFormats={['pdf', 'mp4', 'webm', 'mov']}
          onFilesAdd={handleAddOtherFiles}
        />

        {isEmojiPickerOpen && (
          <div
            className="emoji-picker-container"
            style={{
              position: 'absolute',
              bottom: '100%',
              left: 0,
              zIndex: 9999,
            }}
          >
            <EmojiPicker
              onEmojiClick={handleEmojiClick}
              autoFocusSearch={false}
              emojiStyle="apple"
              height={500}
              width={348}
              previewConfig={{ showPreview: false }}
              skinTonesDisabled={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};
